import { createElement, PropsWithChildren } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';

export type TUseFormReturnWrapped<T> = UseFormReturn<T> & {
  onFormSubmit?: () => unknown;
  isSubmitEnabled: boolean;
};

type TFormProps<T> = JSX.IntrinsicElements['form'] & {
  onSubmit?: SubmitHandler<T>;
  useFormReturn: TUseFormReturnWrapped<T>;
};

export default function Form<T>({
  useFormReturn,
  children,
  onSubmit,
  ...props
}: PropsWithChildren<TFormProps<T>>): JSX.Element {
  const {
    register,
    handleSubmit,
    onFormSubmit,
    formState: { errors },
    isSubmitEnabled,
  } = useFormReturn;

  return (
    <form
      noValidate
      {...props}
      onSubmit={onFormSubmit || (onSubmit && handleSubmit(onSubmit))}
    >
      {Array.isArray(children)
        ? children.map((child) => {
            const { name, control, type } = child?.props || {};
            // when name is specified as prop component is registered and error is added to it's prop
            if (name && !control) {
              return createElement(child.type, {
                ...register(name),
                error: errors[child.props.name as keyof T]?.message,
                key: name,
                id: name,
                ...child.props,
              });
            }
            // when component is probably button disabled prop is added
            if (type === 'submit') {
              return createElement(child.type, {
                disabled: !isSubmitEnabled,
                ...child.props,
              });
            }
            return child;
          })
        : children}
    </form>
  );
}
